import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  MapPin, Eye, Heart, Calendar, Fuel, Gauge, Car, Home, 
  Bed, Bath, Ruler, Sofa, Truck, Check, Plus, ArrowRight,
  Sparkles, Users, Award, Play
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Check if URL is a video
function isVideoUrl(url) {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
  return videoExtensions.some(ext => url.toLowerCase().includes(ext));
}

// Placeholder images for each type when no image provided
const PLACEHOLDER_IMAGES = {
  vehicles: [
    "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1542362567-b07e54358753?w=800&h=600&fit=crop",
  ],
  real_estate: [
    "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&h=600&fit=crop",
  ],
  furniture: [
    "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1538688525198-9b88f6f53126?w=800&h=600&fit=crop",
  ],
  rvs: [
    "https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1626015073031-1a2e2e1de56a?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1561361513-2d000a50f0dc?w=800&h=600&fit=crop",
  ],
};

function getPlaceholderImage(type, index) {
  const images = PLACEHOLDER_IMAGES[type] || PLACEHOLDER_IMAGES.vehicles;
  return images[index % images.length];
}

// Vehicle Card
export function VehicleCard({ listing, onCompare, isSelected, index = 0 }) {
  const primaryMedia = listing.images?.[0] || listing.thumbnail_url || getPlaceholderImage("vehicles", index);
  const isVideo = isVideoUrl(primaryMedia);
  
  const handleClick = () => {
    window.scrollTo(0, 0);
  };
  
  return (
    <Link to={`/marketplace/vehicles/${listing.id}`} className="block" onClick={handleClick}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.03 }}
        className={`glass-card rounded-xl overflow-hidden hover:border-[#EF4444]/50 transition-all group cursor-pointer h-full ${listing.best_of_best ? 'ring-1 ring-[#F59E0B]/70' : ''}`}
      >
        {/* Image or Video */}
        <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#1E293B] to-[#0F172A]">
          {isVideo ? (
            <video
              src={primaryMedia}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onLoadedData={(e) => e.target.play().catch(() => {})}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <img
              src={primaryMedia}
              alt={listing.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.target.src = getPlaceholderImage("vehicles", index); }}
            />
          )}
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {listing.best_of_best && (
              <Badge className="bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white text-xs font-bold px-3 py-1 shadow-lg animate-pulse">
                <Award className="w-3 h-3 mr-1" />
                Best of Best Dealer 2026
              </Badge>
            )}
            {listing.condition && !listing.best_of_best && (
              <Badge className={`text-xs font-semibold ${
                listing.condition.toLowerCase() === "new" ? "bg-[#10B981]" :
                listing.condition.toLowerCase().includes("certified") ? "bg-[#F59E0B]" :
                "bg-[#64748B]"
              }`}>
                {listing.condition}
              </Badge>
            )}
            {listing.featured && !listing.best_of_best && (
              <Badge className="bg-[#7C3AED] text-xs font-semibold">
                <Sparkles className="w-3 h-3 mr-1" />Featured
              </Badge>
            )}
          </div>
          
          {/* Compare checkbox */}
          {onCompare && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCompare(listing); }}
              className={`absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isSelected ? "bg-[#7C3AED] text-white" : "bg-black/50 text-white hover:bg-[#7C3AED]"
              }`}
            >
              {isSelected ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </button>
          )}
          
          {/* Price overlay */}
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-2xl font-bold text-white drop-shadow-lg">
              {listing.price ? `$${listing.price.toLocaleString()}` : "Call for Price"}
            </p>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 pt-3">
          <h3 className="text-white font-bold text-lg mb-2 line-clamp-1 group-hover:text-[#EF4444] transition-colors">
            {listing.year} {listing.make} {listing.model}
          </h3>
          
          <div className="flex flex-wrap items-center gap-3 text-sm text-[#94A3B8] mb-3">
            {listing.mileage !== null && listing.mileage !== undefined && (
              <span className="flex items-center gap-1">
                <Gauge className="w-4 h-4 text-[#00E5FF]" />
                {listing.mileage.toLocaleString()} mi
              </span>
            )}
            {listing.fuel_type && (
              <span className="flex items-center gap-1">
                <Fuel className="w-4 h-4 text-[#10B981]" />
                {listing.fuel_type}
              </span>
            )}
            {listing.transmission && (
              <span className="flex items-center gap-1">
                <Car className="w-4 h-4 text-[#F59E0B]" />
                {listing.transmission}
              </span>
            )}
          </div>
          
          {listing.best_of_best && listing.seller_name && (
            <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
              <Award className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-medium">{listing.seller_name}</span>
            </div>
          )}
          
          {(listing.city || listing.state) && (
            <div className="flex items-center gap-1 text-sm text-[#64748B] mb-4">
              <MapPin className="w-3 h-3" />
              {[listing.city, listing.state].filter(Boolean).join(", ")}
            </div>
          )}
          
          <Button className={`w-full font-semibold transition-all ${
            listing.best_of_best 
              ? 'bg-gradient-to-r from-[#F59E0B] to-[#EF4444] hover:from-[#EF4444] hover:to-[#F59E0B] shadow-lg shadow-[#F59E0B]/30' 
              : 'bg-[#EF4444] hover:bg-[#DC2626] group-hover:shadow-lg group-hover:shadow-[#EF4444]/30'
          } text-white`}>
            View Details <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </Link>
  );
}

// Real Estate Card
export function RealEstateCard({ listing, onCompare, isSelected, index = 0 }) {
  const primaryMedia = listing.images?.[0] || listing.thumbnail_url || getPlaceholderImage("real_estate", index);
  const isVideo = isVideoUrl(primaryMedia);
  
  const handleClick = () => {
    window.scrollTo(0, 0);
  };
  
  return (
    <Link to={`/marketplace/real_estate/${listing.id}`} className="block" onClick={handleClick}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.03 }}
        className={`glass-card rounded-xl overflow-hidden hover:border-[#EC4899]/50 transition-all group cursor-pointer h-full ${listing.best_of_best ? 'ring-1 ring-[#F59E0B]/70' : ''}`}
      >
        {/* Image or Video */}
        <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#1E293B] to-[#0F172A]">
          {isVideo ? (
            <video
              src={primaryMedia}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onLoadedData={(e) => e.target.play().catch(() => {})}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <img
              src={primaryMedia}
              alt={listing.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.target.src = getPlaceholderImage("real_estate", index); }}
            />
          )}
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {listing.best_of_best && (
              <Badge className="bg-gradient-to-r from-[#F59E0B] to-[#EC4899] text-white text-xs font-bold px-3 py-1 shadow-lg animate-pulse">
                <Award className="w-3 h-3 mr-1" />
                Best of Best Realtor 2026
              </Badge>
            )}
            {listing.listing_status && !listing.best_of_best && (
              <Badge className={`text-xs font-semibold ${
                listing.listing_status === "for_sale" ? "bg-[#10B981]" :
                listing.listing_status === "for_rent" ? "bg-[#00E5FF]" :
                listing.listing_status === "pending" ? "bg-[#F59E0B]" :
                "bg-[#64748B]"
              }`}>
                {listing.listing_status.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
            )}
            {listing.property_type && !listing.best_of_best && (
              <Badge className="bg-[#EC4899]/90 text-xs font-semibold">{listing.property_type}</Badge>
            )}
          </div>
          
          {/* Compare checkbox */}
          {onCompare && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCompare(listing); }}
              className={`absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isSelected ? "bg-[#7C3AED] text-white" : "bg-black/50 text-white hover:bg-[#7C3AED]"
              }`}
            >
              {isSelected ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </button>
          )}
          
          {/* Price overlay */}
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-2xl font-bold text-white drop-shadow-lg">
              {listing.price ? `$${listing.price.toLocaleString()}` : "Call for Price"}
              {listing.listing_status === "for_rent" && <span className="text-sm font-normal">/mo</span>}
            </p>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 pt-3">
          <h3 className="text-white font-bold text-lg mb-2 line-clamp-1 group-hover:text-[#EC4899] transition-colors">
            {listing.title || `${listing.bedrooms} Bed ${listing.property_type}`}
          </h3>
          
          <div className="flex items-center gap-4 text-sm text-[#94A3B8] mb-3">
            {listing.bedrooms && (
              <span className="flex items-center gap-1">
                <Bed className="w-4 h-4 text-[#EC4899]" />
                {listing.bedrooms} bd
              </span>
            )}
            {listing.bathrooms && (
              <span className="flex items-center gap-1">
                <Bath className="w-4 h-4 text-[#00E5FF]" />
                {listing.bathrooms} ba
              </span>
            )}
            {listing.sqft && (
              <span className="flex items-center gap-1">
                <Ruler className="w-4 h-4 text-[#10B981]" />
                {listing.sqft.toLocaleString()} sqft
              </span>
            )}
          </div>
          
          {listing.best_of_best && listing.seller_name && (
            <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
              <Award className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-medium">{listing.seller_name}</span>
            </div>
          )}
          
          <div className="flex items-start gap-1 text-sm text-[#64748B] mb-4 min-h-[40px]">
            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-2">
              {listing.address || [listing.city, listing.state].filter(Boolean).join(", ")}
            </span>
          </div>
          
          <Button className={`w-full font-semibold transition-all ${
            listing.best_of_best 
              ? 'bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:from-[#EC4899] hover:to-[#F59E0B] shadow-lg shadow-[#F59E0B]/30' 
              : 'bg-[#EC4899] hover:bg-[#DB2777] group-hover:shadow-lg group-hover:shadow-[#EC4899]/30'
          } text-white`}>
            View Details <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </Link>
  );
}

// RV Card
export function RVCard({ listing, onCompare, isSelected, index = 0 }) {
  const primaryMedia = listing.images?.[0] || listing.thumbnail_url || getPlaceholderImage("rvs", index);
  const isVideo = isVideoUrl(primaryMedia);
  
  const handleClick = () => {
    window.scrollTo(0, 0);
  };
  
  return (
    <Link to={`/marketplace/rvs/${listing.id}`} className="block" onClick={handleClick}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.03 }}
        className={`glass-card rounded-xl overflow-hidden hover:border-[#06B6D4]/50 transition-all group cursor-pointer h-full ${listing.best_of_best ? 'ring-1 ring-[#F59E0B]/70' : ''}`}
      >
        {/* Image or Video */}
        <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#1E293B] to-[#0F172A]">
          {isVideo ? (
            <video
              src={primaryMedia}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onLoadedData={(e) => e.target.play().catch(() => {})}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <img
              src={primaryMedia}
              alt={listing.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.target.src = getPlaceholderImage("rvs", index); }}
            />
          )}
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {listing.best_of_best && (
              <Badge className="bg-gradient-to-r from-[#F59E0B] to-[#06B6D4] text-white text-xs font-bold px-3 py-1 shadow-lg animate-pulse">
                <Award className="w-3 h-3 mr-1" />
                Best of Best RV Dealer 2026
              </Badge>
            )}
            {listing.rv_type && !listing.best_of_best && (
              <Badge className="bg-[#06B6D4] text-xs font-semibold">{listing.rv_type}</Badge>
            )}
            {listing.condition && !listing.best_of_best && (
              <Badge className={`text-xs font-semibold ${listing.condition.toLowerCase() === "new" ? "bg-[#10B981]" : "bg-[#64748B]"}`}>
                {listing.condition}
              </Badge>
            )}
          </div>
          
          {/* Compare checkbox */}
          {onCompare && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCompare(listing); }}
              className={`absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isSelected ? "bg-[#7C3AED] text-white" : "bg-black/50 text-white hover:bg-[#7C3AED]"
              }`}
            >
              {isSelected ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </button>
          )}
          
          {/* Price overlay */}
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-2xl font-bold text-white drop-shadow-lg">
              {listing.price ? `$${listing.price.toLocaleString()}` : "Call for Price"}
            </p>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 pt-3">
          <h3 className="text-white font-bold text-lg mb-2 line-clamp-1 group-hover:text-[#06B6D4] transition-colors">
            {listing.year} {listing.make} {listing.rv_type || listing.model}
          </h3>
          
          <div className="flex items-center gap-4 text-sm text-[#94A3B8] mb-3">
            {listing.sleeps && (
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4 text-[#06B6D4]" />
                Sleeps {listing.sleeps}
              </span>
            )}
            {listing.length && (
              <span className="flex items-center gap-1">
                <Ruler className="w-4 h-4 text-[#10B981]" />
                {listing.length} ft
              </span>
            )}
            {listing.slideouts > 0 && (
              <span className="text-[#F59E0B]">
                {listing.slideouts} Slide{listing.slideouts > 1 ? "s" : ""}
              </span>
            )}
          </div>
          
          {listing.best_of_best && listing.seller_name && (
            <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
              <Award className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-medium">{listing.seller_name}</span>
            </div>
          )}
          
          {(listing.city || listing.state) && (
            <div className="flex items-center gap-1 text-sm text-[#64748B] mb-4">
              <MapPin className="w-3 h-3" />
              {[listing.city, listing.state].filter(Boolean).join(", ")}
            </div>
          )}
          
          <Button className={`w-full font-semibold transition-all ${
            listing.best_of_best 
              ? 'bg-gradient-to-r from-[#F59E0B] to-[#06B6D4] hover:from-[#06B6D4] hover:to-[#F59E0B] shadow-lg shadow-[#F59E0B]/30' 
              : 'bg-[#06B6D4] hover:bg-[#0891B2] group-hover:shadow-lg group-hover:shadow-[#06B6D4]/30'
          } text-white`}>
            View Details <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </Link>
  );
}

// Furniture Card
export function FurnitureCard({ listing, onCompare, isSelected, index = 0 }) {
  const primaryMedia = listing.images?.[0] || listing.thumbnail_url || getPlaceholderImage("furniture", index);
  const isVideo = isVideoUrl(primaryMedia);
  
  const handleClick = () => {
    window.scrollTo(0, 0);
  };
  
  return (
    <Link to={`/marketplace/furniture/${listing.id}`} className="block" onClick={handleClick}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.03 }}
        className={`glass-card rounded-xl overflow-hidden hover:border-[#8B5CF6]/50 transition-all group cursor-pointer h-full ${listing.best_of_best ? 'ring-1 ring-[#F59E0B]/70' : ''}`}
      >
        {/* Image or Video */}
        <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#1E293B] to-[#0F172A]">
          {isVideo ? (
            <video
              src={primaryMedia}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onLoadedData={(e) => e.target.play().catch(() => {})}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <img
              src={primaryMedia}
              alt={listing.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.target.src = getPlaceholderImage("furniture", index); }}
            />
          )}
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
          
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {listing.best_of_best && (
              <Badge className="bg-gradient-to-r from-[#F59E0B] to-[#8B5CF6] text-white text-xs font-bold px-3 py-1 shadow-lg animate-pulse">
                <Award className="w-3 h-3 mr-1" />
                Best of Best Store 2026
              </Badge>
            )}
            {listing.category && !listing.best_of_best && (
              <Badge className="bg-[#8B5CF6] text-xs font-semibold">{listing.category}</Badge>
            )}
            {listing.condition && !listing.best_of_best && (
              <Badge className={`text-xs font-semibold ${listing.condition.toLowerCase() === "new" ? "bg-[#10B981]" : "bg-[#64748B]"}`}>
                {listing.condition}
              </Badge>
            )}
          </div>
          
          {/* Compare checkbox */}
          {onCompare && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCompare(listing); }}
              className={`absolute top-3 right-3 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                isSelected ? "bg-[#7C3AED] text-white" : "bg-black/50 text-white hover:bg-[#7C3AED]"
              }`}
            >
              {isSelected ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </button>
          )}
          
          {/* Price overlay */}
          <div className="absolute bottom-3 left-3 right-3">
            <p className="text-2xl font-bold text-white drop-shadow-lg">
              {listing.price ? `$${listing.price.toLocaleString()}` : "Call for Price"}
            </p>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 pt-3">
          <h3 className="text-white font-bold text-lg mb-2 line-clamp-2 group-hover:text-[#8B5CF6] transition-colors">
            {listing.title}
          </h3>
          
          <div className="flex flex-wrap items-center gap-2 text-sm text-[#94A3B8] mb-3">
            {listing.style && (
              <span className="px-2 py-0.5 bg-white/5 rounded text-xs">{listing.style}</span>
            )}
            {listing.material && (
              <span className="px-2 py-0.5 bg-white/5 rounded text-xs">{listing.material}</span>
            )}
            {listing.room_type && (
              <span className="px-2 py-0.5 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded text-xs">{listing.room_type}</span>
            )}
          </div>
          
          {listing.best_of_best && listing.seller_name && (
            <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20">
              <Award className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-medium">{listing.seller_name}</span>
            </div>
          )}
          
          {(listing.city || listing.state) && (
            <div className="flex items-center gap-1 text-sm text-[#64748B] mb-4">
              <MapPin className="w-3 h-3" />
              {[listing.city, listing.state].filter(Boolean).join(", ")}
            </div>
          )}
          
          <Button className={`w-full font-semibold transition-all ${
            listing.best_of_best 
              ? 'bg-gradient-to-r from-[#F59E0B] to-[#8B5CF6] hover:from-[#8B5CF6] hover:to-[#F59E0B] shadow-lg shadow-[#F59E0B]/30' 
              : 'bg-[#8B5CF6] hover:bg-[#7C3AED] group-hover:shadow-lg group-hover:shadow-[#8B5CF6]/30'
          } text-white`}>
            View Details <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </Link>
  );
}

// Generic Listing Card
export function ListingCard({ listing, listingType, onCompare, isSelected, index = 0 }) {
  switch (listingType) {
    case "vehicles":
      return <VehicleCard listing={listing} onCompare={onCompare} isSelected={isSelected} index={index} />;
    case "real_estate":
      return <RealEstateCard listing={listing} onCompare={onCompare} isSelected={isSelected} index={index} />;
    case "rvs":
      return <RVCard listing={listing} onCompare={onCompare} isSelected={isSelected} index={index} />;
    case "furniture":
      return <FurnitureCard listing={listing} onCompare={onCompare} isSelected={isSelected} index={index} />;
    default:
      return <VehicleCard listing={listing} onCompare={onCompare} isSelected={isSelected} index={index} />;
  }
}

export default ListingCard;
